import { Search, Users } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAdminUsers } from "@/api/hooks/admin";
import { usePageCrumbs } from "@/app/layouts/shellContext";
import { EmptyState } from "@/components/EmptyState";
import { ErrorNotice } from "@/components/ErrorNotice";
import { PageHeader } from "@/components/PageHeader";
import { PageLoading } from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";

/** Staff: every account with its organisations and a link to its sign-in history. */
export function AdminUsersPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const users = useAdminUsers(q.trim());
  usePageCrumbs([{ label: t("common.nav.admin") }, { label: t("admin.users.title") }]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("admin.users.title")} description={t("admin.users.intro")} />
      <div className="relative max-w-md">
        <Search aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-fg-subtle" />
        <Input
          type="search"
          aria-label={t("admin.users.search")}
          placeholder={t("admin.users.search")}
          className="pl-12"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {users.isPending ? <PageLoading /> : null}
      {users.isError ? <ErrorNotice error={users.error} /> : null}
      {users.data && users.data.length === 0 ? (
        <EmptyState icon={<Users className="h-6 w-6" />} title={t("admin.users.empty")} description={t("admin.users.emptyBody")} />
      ) : null}
      {users.data && users.data.length > 0 ? (
        <Table aria-label={t("admin.users.title")}>
          <THead>
            <TR>
              <TH>{t("admin.users.colName")}</TH>
              <TH>{t("admin.users.colEmail")}</TH>
              <TH>{t("admin.users.colOrgs")}</TH>
              <TH>{t("admin.users.colStatus")}</TH>
              <TH>{t("admin.users.colHistory")}</TH>
            </TR>
          </THead>
          <TBody>
            {users.data.map((u) => (
              <TR key={u.id}>
                <TD className="font-medium">{u.name}</TD>
                <TD className="break-all text-fg-muted">{u.email}</TD>
                <TD>
                  {u.is_staff ? (
                    <Badge variant="solid">{t("admin.users.staff")}</Badge>
                  ) : u.memberships.length > 0 ? (
                    <ul className="flex flex-col gap-1">
                      {u.memberships.map((m) => (
                        <li key={m.organisation_id}>
                          <Link to={`/admin/orgs/${m.organisation_id}`}>{m.organisation_name}</Link>
                          <span className="text-sm text-fg-muted"> · {t(`members.role.${m.role}`)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-fg-muted">{t("admin.users.none")}</span>
                  )}
                </TD>
                <TD>
                  {!u.is_active ? (
                    <Badge variant="grey">{t("admin.users.inactive")}</Badge>
                  ) : u.has_password ? (
                    <Badge variant="success">{t("admin.users.active")}</Badge>
                  ) : (
                    <Badge variant="warning">{t("admin.users.invited")}</Badge>
                  )}
                </TD>
                <TD>
                  <Link to={`/admin/users/${u.id}/events`} aria-label={t("admin.users.historyFor", { name: u.name })}>
                    {t("admin.users.history")}
                  </Link>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      ) : null}
    </div>
  );
}
